/**
 * A custom dialog element.
 *
 * @customElement dialog-component
 *
 * @example
 *    <dialog-component>
 *        <div>Dialog content here</div>
 *     </dialog-component>
 **/
export class DialogComponent extends HTMLElement {
    constructor() {
        super();
        /** @type {HTMLDialogElement|null} */
        this.dialog = null;
        this.previousFocus = document.activeElement;
        this.allowOutsideClickClose = true; // default behavior
        this.dialogInnerHeight = 0; // to store the height of the dialog inner element
    }

    static mobileBreakpoint = 490; // Mobile breakpoint for responsive design

    static mobileAnimationDuration = 500; // Animation duration for mobile devices

    static desktopAnimationDuration = 250; // Animation duration for desktop devices

    /**
     * The observed attributes for this element
     * @returns {string[]}
     */
    static observedAttributes = [];

    /**
     * Called when an observed attribute changes.
     * @param {string} name
     * @param {string|null} oldValue
     * @param {string|null} newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
        // No observed attributes yet
    }

    /**
     * Returns the animation duration based on the current viewport width.
     * @returns {number}
     */
    getAnimationDuration() {
        return window.matchMedia(`(max-width: ${DialogComponent.mobileBreakpoint}px)`).matches
            ? DialogComponent.mobileAnimationDuration
            : DialogComponent.desktopAnimationDuration;
    }

    /**
     * Shows the loading state of the dialog.
     * @description
     * This method adds the "active" class to the dialog loading element and disables outside click close.
     * @returns {void}
     */
    showLoading() {
        if (!this.dialog) return;

        const dialogLoadingEl = this.dialog.querySelector('.dialogLoading');

        // Disable outside click close
        this.allowOutsideClickClose = false;

        // Disable all close buttons
        const closeButtons = this.dialog.querySelectorAll(`[data-action="close-dialog"]`);

        for (const button of closeButtons) {
            button.disabled = true; // Disable close buttons
        }

        dialogLoadingEl.setAttribute('aria-hidden', 'false');
        dialogLoadingEl.classList.add("active"); // Show the loading element
    }

    /**
     * Hides the loading state of the dialog.
     * @description
     * This method hides the loading element by removing the "active" class and re-enables outside click close.
     * @returns {void}
     */
    hideLoading() {
        if (!this.dialog) return;

        const dialogLoadingEl = this.dialog.querySelector('.dialogLoading');

        // Allow outside click close
        this.allowOutsideClickClose = true;

        // Re-enable all close buttons
        const closeButtons = this.dialog.querySelectorAll(`[data-action="close-dialog"]`);

        for (const button of closeButtons) {
            button.disabled = false; // Re-enable close buttons
        }

        dialogLoadingEl.setAttribute('aria-hidden', 'true');
        dialogLoadingEl.classList.remove("active"); // Hide the loading element
    }

    /**
     * Opens the dialog
     * @description
     * Opens the dialog dialog element.
     * If the dialog is already open, it does nothing.
     * When the dialog is opened a custom event `dialog-opened` is dispatched.
     * @returns {Promise<void>} - A promise that resolves when the dialog is opened.
     */
    async open() {
        if (!this.dialog || this.dialog.open) return;

        // Reset the backdrop opacity CSS variable
        this.dialog.style.setProperty('--dialog-component-backdrop-opacity', "1");

        // Reset the backdrop blur CSS variable
        this.dialog.style.setProperty('--dialog-component-backdrop-blur', `5px`);


        this.dialog.showModal();

        // Ensure starting styles apply before we flip to the "open" state
        this.dialog.classList.remove('open');

        // Force a reflow so Safari/iOS commits the initial computed style
        void this.dialog.getBoundingClientRect();

        // Wait a frame, then add the class that changes transform/opacity
        await new Promise(requestAnimationFrame);

        // apply open class to start animating
        this.dialog.classList.add('open');

        // try to focus dialog
        try {
            this.dialog.focus?.();
        } catch {
        }

        // Wait for the CSS transition to finish
        await new Promise(resolve => setTimeout(resolve, this.getAnimationDuration()));

        // prevent scrolling
        this.lockScroll();

        // Set the open attribute
        this.setAttribute("data-open", "true");

        // Get and store the height of the dialog inner element
        const dialogInner = this.dialog.querySelector(".dialogInner");
        if (dialogInner) {
            this.dialogInnerHeight = dialogInner.getBoundingClientRect().height;
        }

        // Dispatch the dialog-opened event
        this.dispatchEvent(new CustomEvent("dialog-opened"));
    }

    /**
     * Closes the dialog
     * @description
     * Closes the dialog dialog element.
     * If the dialog is not open, it does nothing.
     * When the dialog is closed a custom event `dialog-closed` is dispatched.
     * @returns {Promise<void>} - A promise that resolves when the dialog is closed.
     */
    async close() {
        if (!this.dialog || !this.dialog.open) return;

        // Kick off the transition back to the starting state
        this.dialog.classList.remove('open');

        // Make sure Safari sees the change before waiting
        await new Promise(requestAnimationFrame);
        await new Promise(resolve => setTimeout(resolve, this.getAnimationDuration()));

        this.dialog.close();

        try {
            this.previousFocus?.focus?.();
        } catch {
        }

        this.unlockScroll();
        this.removeAttribute('data-open');

        const dialogInner = this.dialog.querySelector('.dialogInner');
        if (dialogInner) dialogInner.style.transform = '';

        this.dispatchEvent(new CustomEvent('dialog-closed'));
    }


    /**
     * Locks the page scroll, freezing the background while allowing
     * dialog content or other fixed containers to remain scrollable.
     * @description
     * How it works:
     * 1. Saves the current vertical scroll position (`window.scrollY`).
     * 2. Sets the <body> to `position: fixed` and offsets it by the negative
     *    scroll value to keep the viewport visually in the same place.
     * 3. Sets `width: 100%` to prevent horizontal layout shifts.
     * 4. Adds a CSS class (`dialogOpen`) for any additional styling or touch-action
     *    handling.
     */
    lockScroll() {
        this.scrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.width = "100%";
        document.body.classList.add("dialogOpen");
    }

    /**
     * Unlocks the page scroll, restoring normal scrolling behavior.
     * @description
     * How it works:
     * 1. Removes the `position`, `top`, and `width` styles
     *    applied by `lockScroll()`.
     * 2. Removes the `dialogOpen` CSS class.
     * 3. Restores the previous scroll position saved in `this.scrollY`.
     *
     * This ensures that after closing a dialog or overlay, the user returns
     * to the same place they were before the scroll was locked.
     */
    unlockScroll() {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.classList.remove("dialogOpen");
        window.scrollTo(0, this.scrollY);
    }

    /**
     * Closes the Dialog on outside click
     * @description
     * This method checks if the click event target is the dialog itself,
     * if so, it closes the dialog.
     * @param {Event} event
     * @returns {void}
     */
    async closeDialogOnOutsideClick(event) {
        if (!this.allowOutsideClickClose) return;
        if (event.target !== this.dialog) return;
        await this.close();
    }

    /**
     * Scrolls the dialog content to the top.
     */
    scrollToTop() {
        if (!this.dialog) return;
        const dialogContentInner = this.dialog.querySelector(".dialogContent");
        if (dialogContentInner) {
            dialogContentInner.scrollTop = 0;
        }
    }

    setupDragBar() {
        const dialogInner = this.querySelector(".dialogInner");

        // Drag Bar Element
        const dialogDragBar = this.querySelector(".dialogDragBar");


        let startY = 0;
        let currentTranslateY = 0;
        let dragging = false;
        let dialogHeight = 0; // cache height
        let pending = false; // rAF throttle
        const thresholdPercent = 0.30;
        const MAX_BLUR = 5;
        const OPACITY_MULTIPLIER = 1.3;

        const updateDragBarAria = (isDragging) => {
            if (isDragging) {
                dialogDragBar.setAttribute("aria-grabbed", "true");
            } else {
                dialogDragBar.setAttribute("aria-grabbed", "false");
            }
        }

        const getClientY = (e) =>
            e.type.startsWith("touch")
                ? e.touches[0]?.clientY || e.changedTouches[0]?.clientY
                : e.clientY;

        const updateStyles = () => {
            const dragPercent = currentTranslateY / dialogHeight;

            // Translate
            dialogInner.style.transform = `translateY(${currentTranslateY}px)`;

            // Blur (5px → 0px)
            const newBlur = Math.max(0, MAX_BLUR * (1 - dragPercent));
            this.dialog.style.setProperty('--dialog-component-backdrop-blur', `${newBlur}px`);

            // Opacity (1 → 0)
            const newOpacity = Math.max(0, 1 - dragPercent * OPACITY_MULTIPLIER);
            this.dialog.style.setProperty('--dialog-component-backdrop-opacity', newOpacity.toString());

            pending = false;
        };

        const onMove = (e) => {
            if (!dragging) return;

            let dragDistance = getClientY(e) - startY;
            if (dragDistance < 0) dragDistance = 0;
            currentTranslateY = dragDistance;

            // Use requestAnimationFrame to throttle visual updates
            if (!pending) {
                pending = true;
                requestAnimationFrame(updateStyles);
            }

            if (e.cancelable) e.preventDefault();
        };

        const onEnd = async () => {
            if (!dragging) return;

            updateDragBarAria(dragging);

            const dragPercent = currentTranslateY / dialogHeight;

            if (dragPercent > thresholdPercent) {
                cleanup();
                await this.close();
            } else {
                dialogInner.style.transform = "translateY(0)";
                cleanup();
            }
        };

        const cleanup = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onEnd);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onEnd);
            window.removeEventListener("touchcancel", onEnd);
        };

        const onStart = (e) => {
            dragging = true;
            updateDragBarAria(dragging);
            startY = getClientY(e);
            currentTranslateY = 0;
            dialogHeight = this.dialogInnerHeight; // cache height at drag start
            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onEnd);
            window.addEventListener("touchmove", onMove, {passive: false});
            window.addEventListener("touchend", onEnd);
            window.addEventListener("touchcancel", onEnd);
        };

        dialogDragBar.addEventListener("pointerdown", onStart);
        dialogDragBar.addEventListener("touchstart", onStart, {passive: false});
    }

    /**
     * Called when the element is connected to the DOM.
     * Creates dialog structure with `.dialogInner` and `.dialogContent`.
     */
    connectedCallback() {
        // Save children
        const fragment = document.createDocumentFragment();
        while (this.firstChild) {
            fragment.appendChild(this.firstChild);
        }

        // Create dialog structure
        this.dialog = document.createElement("dialog");
        this.dialog.classList.add("dialog");
        this.dialog.innerHTML = `
            <div class="dialogInner">
                <div class="dialogDragBar" role="button" aria-label="Drag handle to move or close dialog" aria-grabbed="false" tabindex="0"></div>
                <button class="dialogCloseButton" aria-label="Close dialog" data-action="close-dialog">
                    <svg role="presentation" viewBox="0 0 16 14">
                        <path d="M15 0L1 14m14 0L1 0" fill-rule="evenodd" fill="none" stroke="currentColor"></path>
                    </svg>
                </button>
                <div class="dialogLoading" aria-hidden="true">
                    <div class="dialogLoadingSpinner"></div>
                </div>
                <div class="dialogContent"></div>
            </div>
        `;

        // Event listeners
        this.dialog.addEventListener("click", this.closeDialogOnOutsideClick.bind(this));
        this.dialog.querySelectorAll(`[data-action="close-dialog"]`)
            .forEach(btn => btn.addEventListener("click", async () => await this.close()));

        // append fragment html to the dialog content
        if (fragment) {
            this.dialog.querySelector(".dialogContent").appendChild(fragment);
        }

        // attach html
        this.prepend(this.dialog);

        // setup drag bar
        this.setupDragBar();
    }

    /**
     * Called when the element is disconnected from the DOM.
     */
    disconnectedCallback() {
        if (this.dialog) {
            this.dialog.removeEventListener("click", this.closeDialogOnOutsideClick);
        }
        if (this.dialog?.getAttribute("data-open") === "true") {
            document.body.classList.remove("dialogOpen");
        }
    }
}

if (!customElements.get("dialog-component")) {
    customElements.define("dialog-component", DialogComponent);
}
